/**
 * CAS-versioned server sync for a store slice — phase 4.3
 * (DESIGN-phase4.md §7). The server owns the truth (paper_state blob with
 * optimistic concurrency: every save names the version it read; a mismatch
 * is a 409 and the server's copy wins). This controller owns the CLIENT
 * half of that contract in one place: version bookkeeping, save
 * coalescing, and conflict handling — previously scattered across
 * paperStateVersion / loadState / saveState / adoptServerState /
 * handleServerStateConflict in static/paper.html.
 *
 * Save coalescing: at most ONE save request is in flight; a save requested
 * while one is running becomes a single trailing save of the LATEST value
 * once it settles. This also removes a real race the old code had: two
 * overlapping saveState() POSTs carried the same base_version, so one
 * always 409'd and triggered a disruptive conflict-reload for no reason.
 */

export interface CasLoadResult<T> {
  value: T;
  version: number;
}

export type CasSaveResult =
  | { status: "saved"; version: number }
  | { status: "conflict" }
  | { status: "error" };

export interface CasSyncOpts<T> {
  /** Fetch the server's current value+version (GET). */
  load: () => Promise<CasLoadResult<T> | null>;
  /** Persist `value` named against `baseVersion` (POST). */
  save: (value: T, baseVersion: number) => Promise<CasSaveResult>;
  /** Current client value to persist (read at send time, not queue time). */
  getValue: () => T;
  /** Adopt a server value into client state (store.set + re-render hook). */
  onAdopt: (value: T, version: number) => void;
  /** A save hit a 409 — the server's copy won. Controller reloads first;
   * this hook is for page-level re-render/toast. */
  onConflict?: () => void;
  /** Gate saves (e.g. read-only tabs must never persist). */
  canSave?: () => boolean;
}

export interface CasSync<T> {
  load(): Promise<boolean>;
  /** Request a save of the current value. Coalesced; safe to call freely. */
  requestSave(): void;
  /** Adopt an out-of-band server response (e.g. an order API's state+version). */
  adopt(value: T, version: number): void;
  version(): number;
  saving(): boolean;
}

export function createCasSync<T>(opts: CasSyncOpts<T>): CasSync<T> {
  let version = 0;
  let inFlight = false;
  let trailing = false;

  async function doSave(): Promise<void> {
    if (inFlight) {
      trailing = true;
      return;
    }
    if (opts.canSave && !opts.canSave()) return;
    inFlight = true;
    try {
      const result = await opts.save(opts.getValue(), version);
      if (result.status === "saved") {
        version = result.version;
      } else if (result.status === "conflict") {
        // Server's copy wins — reload it, then tell the page.
        await load();
        opts.onConflict?.();
      }
      // 'error': transient network/server issue — keep the local value and
      // version; the next requestSave() (every mutation site calls one) or
      // the version poll's reload will converge.
    } finally {
      inFlight = false;
      if (trailing) {
        trailing = false;
        void doSave();
      }
    }
  }

  async function load(): Promise<boolean> {
    const loaded = await opts.load();
    if (!loaded) return false;
    version = loaded.version;
    opts.onAdopt(loaded.value, loaded.version);
    return true;
  }

  return {
    load,
    requestSave() {
      void doSave();
    },
    adopt(value, v) {
      version = v;
      opts.onAdopt(value, v);
    },
    version: () => version,
    saving: () => inFlight,
  };
}
